import React, {Component} from 'react';
import {Grid, Header, Button, Icon, Table, Tab} from 'semantic-ui-react';
import toastr from 'toastr';
import styled, {css} from 'react-emotion';
import moment from 'moment';

import RequiresRole, {ROLES} from '../security/RequiresRole';
import epicorApi from '../../store/services/epicor';
import documentsService from '../../store/services/documents';
import reportsService from '../../store/services/reports';
import favoritesService from '../../store/services/favorites';
import Layout from '../layout/Layout';
import LoadingContainer from '../layout/LoadingContainer';
import Page from '../layout/Page';
import {some} from 'lodash';

const Styles = styled.div`
  .ui.button.download-button {
    background: #e3af25;
    color: #fff;
    display: flex;
    align-items: center;
    text-align: left;
    margin-bottom: 10px;
    min-height: 45px;
    border-radius: 0;

    > i.icon {
      margin-right: 15px !important;
    }
  }
`;

export const documentTypeTitle = {
  default: 'Document',
  TestChart: 'Test Chart',
  DataBook: 'Complete Data Book',
  InspectionReport: 'Inspection Report',
  VendorCOC: 'Connection COC', // Changed from 'Vendor COC' to 'Connection COC' per Kelley's request.
  CalculationSummary: 'Calculation Summary',
  CustomerPrint: 'Customer Print',
  MaterialCert: 'Material Certification',
  MakeBreak: 'Make Breaks',
  DiagramPage: 'General Assembly Drawing',
  MaintenanceManual: 'Maintenance Manual',
  AssemblyAndDisassembly: 'Assembly & Disassembly',
  GuideToEvaluatingParts: 'Guide To Evaluating Parts'
};

const DocumentLink = ({id, title, type, loading = false, onClick}) => (
  <React.Fragment>
    <dt>
      <a
        role="button"
        className={css`
          cursor: pointer;
          i {
            margin-right: 0.5em;
          }
        `}
        disabled={loading}
        onClick={onClick}
      >
        <Icon name="download" />
        {documentTypeTitle[type] || documentTypeTitle['default']}
      </a>
    </dt>
    <dd />
  </React.Fragment>
);

export class SerialNumberDetail extends Component {
  state = {
    detail: undefined,
    documents: undefined,
    downloading: false,
    loadingConformanceReport: false,
    loadingPartsList: false,
    loadingCurrentRevisionPartsList: false,
    loadingOtherRevisionsPartsList: false,
  };

  componentDidMount() {
    this._loadData(this.props.sysRowId);
  }

  componentDidUpdate(prevProps) {
    if (prevProps.sysRowId !== this.props.sysRowId) {
      this._loadData(this.props.sysRowId);
    }
  }

  async _loadData() {
    try {
      const {sysRowId} = this.props;
      this.setState({detail: undefined, documents: undefined});
      const detail = (await epicorApi.fetchSerial(this.props.sysRowId)).result;
      if (this.props.sysRowId === sysRowId) {
        this.setState({detail});
        const {
          serialNumber,
          partNum,
          jobNum,
          splitSourceJobNum,
          gadwgCode,
          machiningWorkOrderNums,
          materialNums,
          maintenanceManual,
          assemblyAndDisassembly,
        } = detail;
        const documents = (
          await documentsService.enumerate({
            serialNumber,
            partNum,
            jobNum,
            splitSourceJobNum,
            gadwgCode,
            machiningWorkOrderNums,
            materialNums,
            maintenanceManual,
            assemblyAndDisassembly,
          })
        ).result;

        if (this.props.sysRowId === sysRowId) {
          this.setState({documents});
        }
      }
    } catch (error) {
      this.setState({error});
    }
  }

  _getFileTypeName = fileType => {
    if (fileType === 'VendorCOC') return 'ConnectionCOC';

    return fileType;
  };

  _downloadDocuments = async (files, fileName) => {
    if (this.state.downloading) return;

    const baseUrl = process.env.REACT_APP_API_URL || '';

    try {
      const token = (
        await documentsService.createReportToken({
          documents: files,
        })
      ).result;
      if (token) {
        var url = `${baseUrl}documents/download/${token}?fileName=${fileName}`;
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
      }
      this.setState({downloading: false});
    } catch (error) {
      this.setState({error, downloading: false});
    }
  };

  _downloadAllDocuments = async (sysRowId, files, fileName) => {
    if (this.state.downloading) return;

    const baseUrl = process.env.REACT_APP_API_URL || '';

    try {
      this.setState({downloading: true});
      const tokens = (
        await documentsService.createAllReportTokens(sysRowId, {
          documents: files,
        })
      ).result;
      if (tokens) {
        const tokenUrl = tokens.join('&reportTokens=');
        const url = `${baseUrl}documents/download-all/?reportTokens=${tokenUrl}&fileName=${fileName}`;

        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
      }
      setTimeout(() => {
        this.setState({downloading: false});
      }, 12000);
    } catch (error) {
      this.setState({error, downloading: false});
    }
  };

  _openConformanceReport = async () => {
    const {sysRowID} = this.state.detail;

    try {
      this.setState({loadingConformanceReport: true});

      const reportUrl = await reportsService.createConformanceReportUrl(
        sysRowID
      );

      if (!reportUrl) {
        throw reportUrl;
      }

      const link = document.createElement('a');
      link.href = reportUrl;
      link.setAttribute('download', `mmvalve_certificate_${sysRowID}.pdf`);
      document.body.appendChild(link);
      link.click();
      setTimeout(() => {
        this.setState({loadingConformanceReport: false});
      }, 3000);
    } catch (error) {
      toastr.error('Unable to retrieve report');

      this.setState({loadingConformanceReport: false});
    }
  };

  _openPartListReport = async () => {
    const {sysRowID} = this.state.detail;

    try {
      this.setState({loadingPartsList: true});
      const reportUrl = await reportsService.createPartListReportUrl(sysRowID);

      if (!reportUrl) {
        throw reportUrl;
      }

      const link = document.createElement('a');
      link.href = reportUrl;
      link.setAttribute('download', `mmvalve_partlist_${sysRowID}.pdf`);
      document.body.appendChild(link);
      link.click();
      setTimeout(() => {
        this.setState({loadingPartsList: false});
      }, 3000);
    } catch (error) {
      toastr.error('Unable to retrieve report');
      this.setState({loadingPartsList: false});
    }
  };

  _openCurrentRevisionPartListReport = async () => {
    const {sysRowID} = this.state.detail;

    try {
      this.setState({loadingCurrentRevisionPartsList: true});
      const reportUrl = await reportsService.createCurrentRevisionPartListReportUrl(
        sysRowID
      );

      if (!reportUrl) {
        throw reportUrl;
      }

      const link = document.createElement('a');
      link.href = reportUrl;
      link.setAttribute(
        'download',
        `mmvalve_currentrevision_partlist_${sysRowID}.pdf`
      );
      document.body.appendChild(link);
      link.click();
      setTimeout(() => {
        this.setState({loadingCurrentRevisionPartsList: false});
      }, 3000);
    } catch (error) {
      toastr.error('Unable to retrieve report');
      this.setState({loadingCurrentRevisionPartsList: false});
    }
  };

  _openOtherRevisionsPartListReport = async () => {
    const {sysRowID} = this.state.detail;

    try {
      this.setState({loadingOtherRevisionsPartsList: true});
      const reportUrl = await reportsService.createOtherRevisionsPartListReportUrl(
        sysRowID
      );

      if (!reportUrl) {
        throw reportUrl;
      }

      const link = document.createElement('a');
      link.href = reportUrl;
      link.setAttribute(
        'download',
        `mmvalve_otherrevisions_partlist_${sysRowID}.pdf`
      );
      document.body.appendChild(link);
      link.click();
      setTimeout(() => {
        this.setState({loadingOtherRevisionsPartsList: false});
      }, 3000);
    } catch (error) {
      toastr.error('Unable to retrieve report');
      this.setState({loadingOtherRevisionsPartsList: false});
    }
  };

  _openCanisterGuardDoc = async () => {
    const {canisterGuardLink} = this.state.detail;

    try {
      window.open(canisterGuardLink, '_blank').focus();
    } catch (error) {
      toastr.error('Unable to retrieve report');
    }
  };

  _openLicensesDoc = async () => {
    const {licensesLink} = this.state.detail;

    try {
      window.open(licensesLink, '_blank').focus();
    } catch (error) {
      toastr.error('Unable to retrieve report');
    }
  };

  _toggleFavorite = async () => {
    const {
      hasFavorite,
      favoriteId = 1,
      serialNumber,
      sysRowID,
    } = this.state.detail;
    try {
      if (hasFavorite) {
        await favoritesService.unfavorite(favoriteId);
        toastr.success('Favorite Removed');
      } else {
        await favoritesService.favorite({
          serialNum: serialNumber,
          sysRowId: sysRowID,
        });
        toastr.success('Favorite Added');
      }
      this._loadData();
    } catch (error) {
      toastr.error('Unable to favorite');
    }
  };

  render() {
    const Repairs = props => {
      const {detail, loadingDetail} = {...props};

      return (
        <Layout.Fill padded>
          <dl>
            <dt>Repair Information:</dt>
            <dd>
              <Table
                striped
                compact
                basic="very"
                unstackable
                style={{marginTop: 10, marginBottom: 30}}
              >
                <Table.Header>
                  <Table.Row>
                    <Table.HeaderCell style={{width: 55}}>
                      Job ID
                    </Table.HeaderCell>
                    <Table.HeaderCell style={{width: 95}}>
                      Can be Repaired
                    </Table.HeaderCell>
                    <Table.HeaderCell style={{width: 100}}>
                      Level of Service
                    </Table.HeaderCell>
                    <Table.HeaderCell style={{width: 40}}>
                      Closed
                    </Table.HeaderCell>
                    <Table.HeaderCell style={{width: 75}}>
                      Certificate
                    </Table.HeaderCell>
                    <Table.HeaderCell style={{width: 50}}>NDE</Table.HeaderCell>
                    <Table.HeaderCell style={{width: 85}}>
                      NDE Recheck
                    </Table.HeaderCell>
                    <Table.HeaderCell style={{width: 75}}>
                      Test Chart
                    </Table.HeaderCell>
                  </Table.Row>
                </Table.Header>
                <tbody>
                  {detail.repairInfo &&
                    some(detail.repairInfo) &&
                    !loadingDetail &&
                    detail.repairInfo.map(x => (
                      <Table.Row key={x.jobId}>
                        <Table.Cell>{x.jobId}</Table.Cell>
                        <Table.Cell>
                          {x.canBeRepaired ? 'Yes' : 'No'}
                        </Table.Cell>
                        <Table.Cell>{x.levelOfService}</Table.Cell>
                        <Table.Cell>
                          {moment(x.dateClosed).format('L')}
                        </Table.Cell>
                        <Table.Cell>
                          {x.certificateUrl ? <a
                            href={x.certificateUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            Open
                          </a> : "N/A"}
                        </Table.Cell>
                        <Table.Cell>
                          {x.ndeUrl ? <a
                            href={x.ndeUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            Open
                          </a> : "N/A"}
                        </Table.Cell>
                        <Table.Cell>
                          {x.ndeRecheckUrl ? <a
                            href={x.ndeRecheckUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            Open
                          </a> : "N/A"}
                        </Table.Cell>
                        <Table.Cell>
                          {x.testChartUrl ? <a
                            href={x.testChartUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            Open
                          </a> : "N/A"}
                        </Table.Cell>
                      </Table.Row>
                    ))}
                  {!loadingDetail && !some(detail.repairInfo) && (
                    <Table.Row>
                      <Table.Cell textAlign="center" colSpan={8}>
                        No Repair Information
                      </Table.Cell>
                    </Table.Row>
                  )}
                </tbody>
              </Table>
            </dd>
          </dl>
        </Layout.Fill>
      );
    };

    const Parts = props => {
      const {detail, loadingDetail} = {...props};

      return (
        <Layout.Fill padded>
          {detail.currentPartListRevision &&
            detail.currentPartListRevision.revisionNum && (
            <div>
                The below parts list (REV{' '}
              {detail.currentPartListRevision.revisionNum.replace('W', '')})
                is current and should be referenced for any replacement parts
                requests.
            </div>
          )}
          <dl>
            <dt>Parts:</dt>
            <dd>
              <Table
                striped
                compact
                basic="very"
                unstackable
                style={{marginTop: 10, marginBottom: 30}}
              >
                <Table.Header>
                  <Table.Row>
                    <Table.HeaderCell style={{width: 50}}>Qty</Table.HeaderCell>
                    <Table.HeaderCell style={{width: 150}}>
                      Part Number
                    </Table.HeaderCell>
                    <Table.HeaderCell>Part Description</Table.HeaderCell>
                  </Table.Row>
                </Table.Header>
                <tbody>
                  {detail.currentPartListRevision &&
                    !!detail.currentPartListRevision.parts.length &&
                    !loadingDetail &&
                    detail.currentPartListRevision.parts.map(x => (
                      <Table.Row key={x.partNum}>
                        <Table.Cell>{x.quantity}</Table.Cell>
                        <Table.Cell>{x.partNum}</Table.Cell>
                        <Table.Cell>{x.partDescription}</Table.Cell>
                      </Table.Row>
                    ))}
                  {(!detail.currentPartListRevision ||
                    !detail.currentPartListRevision.parts ||
                    !detail.currentPartListRevision.parts.length) &&
                    !loadingDetail && (
                    <Table.Row>
                      <Table.Cell textAlign="center" colSpan={3}>
                          No Parts
                      </Table.Cell>
                    </Table.Row>
                  )}
                </tbody>
              </Table>
            </dd>
          </dl>
        </Layout.Fill>
      );
    };

    const PartKits = props => {
      const {detail, loadingDetail} = {...props};
      return (
        <Layout.Fill padded>
          <dl>
            <dt>Part Kits:</dt>
            <dd>
              <Table
                striped
                compact
                basic="very"
                unstackable
                style={{marginTop: 10, marginBottom: 30}}
              >
                <Table.Header>
                  <Table.Row>
                    <Table.HeaderCell style={{width: 150}}>
                      Part Number
                    </Table.HeaderCell>
                    <Table.HeaderCell>Part Description</Table.HeaderCell>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {!!detail.partKits.length &&
                    !loadingDetail &&
                    detail.partKits.map(x => (
                      <Table.Row key={x.partNum}>
                        <Table.Cell>{x.partNum}</Table.Cell>
                        <Table.Cell>{x.partDescription}</Table.Cell>
                      </Table.Row>
                    ))}

                  {!detail.partKits.length && !loadingDetail && (
                    <Table.Row>
                      <Table.Cell textAlign="center" colSpan={2}>
                        No Part Kits
                      </Table.Cell>
                    </Table.Row>
                  )}
                </Table.Body>
              </Table>
            </dd>
          </dl>
        </Layout.Fill>
      );
    };

    const GeneralInfo = props => {
      const {
        loadingDetail,
        loadingDocuments,
        detail,
        documents,
        error,
        downloading,
        loadingPartsList,
        loadingCurrentRevisionPartsList,
        loadingOtherRevisionsPartsList,
        loadingConformanceReport,
      } = {...props};

      return (
        <Layout.Fill padded>
          <LoadingContainer hideContentWhenLoading loading={loadingDetail}>
            {!loadingDetail &&
              (error ? (
                <p>
                  An error occurred while looking up details for this serial
                  number. For help, please contact us at{' '}
                  <a href="mailto:portalsupport@mmvalve.com">
                    portalsupport@mmvalve.com
                  </a>
                  .
                </p>
              ) : (
                <Styles>
                  <Grid stackable columns={2}>
                    <Grid.Column>
                      <div style={{display: 'flex', flexDirection: 'row'}}>
                        <div style={{flex: 1}}>
                          <dl>
                            {detail.orderNum && (
                              <React.Fragment>
                                <RequiresRole
                                  roles={[
                                    ROLES.CUSTOMER_ADMIN,
                                    ROLES.CUSTOMER_USER,
                                    ROLES.SYSTEM_ADMIN,
                                    ROLES.MM_ADMIN,
                                  ]}
                                >
                                  <dt>Order #:</dt>
                                  <dd>
                                    {detail.orderNum > 0
                                      ? detail.orderNum
                                      : 'N/A'}
                                  </dd>
                                </RequiresRole>
                              </React.Fragment>
                            )}

                            <dt>Part #:</dt>
                            <dd>{detail.partNum}</dd>

                            <dt>Rev #:</dt>
                            <dd>
                              {detail.revisionNum} (Original Manufactured)
                            </dd>
                          </dl>
                        </div>
                        <div style={{flex: 1}}>
                          <dl>
                            <dt>Production Date:</dt>
                            <dd>
                              {detail.testDate
                                ? moment(detail.testDate).format('L')
                                : 'N/A'}
                            </dd>

                            <dt>Assembly WO #:</dt>
                            <dd>{detail.jobNum || 'N/A'}</dd>

                            <dt>Machining WO #:</dt>
                            <dd>
                              {detail.machiningWorkOrderNums.length
                                ? detail.machiningWorkOrderNums.join(', ')
                                : detail.splitSourceJobNum
                                  ? detail.splitSourceJobNum
                                  : detail.jobNum
                                    ? detail.jobNum
                                    : 'N/A'}
                            </dd>

                            <dt>Material Lot #:</dt>
                            <dd>
                              {detail.materialNums.length
                                ? detail.materialNums.join(', ')
                                : 'N/A'}
                            </dd>
                          </dl>
                        </div>
                      </div>
                      <dl>
                        <dt>Description:</dt>
                        <dd>{detail.partDescription}</dd>
                      </dl>
                    </Grid.Column>
                    <Grid.Column>
                      {detail.testDate && (
                        <Button
                          className="download-button"
                          fluid
                          icon
                          loading={loadingConformanceReport}
                          onClick={() => this._openConformanceReport()}
                        >
                          <Icon name="download" />
                          <span>CERTIFICATE</span>
                        </Button>
                      )}
                      {detail.licensesLink && (
                        <Button
                          className="download-button"
                          fluid
                          icon
                          onClick={() => this._openLicensesDoc()}
                        >
                          <Icon name="download" />
                          <span>API/ISO/Q1 LICENSE</span>
                        </Button>
                      )}
                      {detail.canisterGuardLink && (
                        <Button
                          className="download-button"
                          fluid
                          icon
                          onClick={() => this._openCanisterGuardDoc()}
                        >
                          <Icon name="download" />
                          <span>CANISTER GUARD®</span>
                        </Button>
                      )}
                      <Button
                        className="download-button"
                        fluid
                        icon
                        loading={loadingPartsList}
                        onClick={() => this._openPartListReport()}
                      >
                        <Icon name="download" />
                        <span>PARTS LIST - ORIGINAL MANUFACTURED</span>
                      </Button>
                      {!!detail.currentRevision &&
                        !!detail.currentRevision.parts.length && (
                        <Button
                          className="download-button"
                          fluid
                          icon
                          loading={loadingCurrentRevisionPartsList}
                          onClick={() =>
                            this._openCurrentRevisionPartListReport()
                          }
                        >
                          <Icon name="download" />
                          <span>PARTS LIST - CURRENT REVISION</span>
                        </Button>
                      )}
                      {!!detail.otherRevisions &&
                        !!detail.otherRevisions.length && (
                        <Button
                          className="download-button"
                          fluid
                          icon
                          loading={loadingOtherRevisionsPartsList}
                          onClick={() =>
                            this._openOtherRevisionsPartListReport()
                          }
                        >
                          <Icon name="download" />
                          <span>PARTS LIST - OTHER REVISIONS</span>
                        </Button>
                      )}
                      <div style={{display: 'flex', flexDirection: 'row'}}>
                        <div style={{flex: 1}}>
                          <dl>
                            <dt>ADDITIONAL RESOURCES:</dt>
                            {!!documents &&
                              documents.filter(
                                x =>
                                  (x.documentType === 'MaintenanceManual') |
                                  (x.documentType === 'AssemblyAndDisassembly') |
                                  (x.documentType === 'GuideToEvaluatingParts')
                              ).length === 0 && (
                              <span>No Additional Resources Available</span>
                            )}
                            <dd />
                            {!!documents &&
                              documents
                                .filter(
                                  x =>
                                    (x.documentType === 'MaintenanceManual') |
                                    (x.documentType ===
                                      'AssemblyAndDisassembly') |
                                    (x.documentType === 'GuideToEvaluatingParts')
                                )
                                .map(
                                  ({
                                    fileId,
                                    title,
                                    documentType,
                                    extension,
                                  }) => (
                                    <DocumentLink
                                      key={fileId}
                                      id={fileId}
                                      title={`${title}.${extension}`}
                                      loading={downloading}
                                      type={documentType}
                                      onClick={() =>
                                        this._downloadDocuments(
                                          [fileId],
                                          `mmvalve_${detail.serialNumber}_${documentType}.pdf`
                                        )
                                      }
                                    />
                                  )
                                )}
                          </dl>
                        </div>
                      </div>
                    </Grid.Column>
                  </Grid>

                  <LoadingContainer
                    hideContentWhenLoading
                    loading={loadingDocuments}
                    message="Loading Documents..."
                  >
                    <dl>
                      {!!documents &&
                        documents
                          .filter(
                            x =>
                              x.documentType !== 'MaintenanceManual' &&
                              x.documentType !== 'AssemblyAndDisassembly' &&
                              x.documentType !== 'GuideToEvaluatingParts'
                          )
                          .map(({fileId, title, documentType, extension}) => (
                            <DocumentLink
                              key={fileId}
                              id={fileId}
                              title={`${title}.${extension}`}
                              loading={downloading}
                              type={documentType}
                              onClick={() =>
                                this._downloadDocuments(
                                  [fileId],
                                  `mmvalve_${
                                    detail.serialNumber
                                  }_${this._getFileTypeName(documentType)}.pdf`
                                )
                              }
                            />
                          ))}
                    </dl>
                    {!!documents && !!documents.length && (
                      <Button
                        className="download-button"
                        icon
                        primary
                        fluid
                        disabled={downloading}
                        loading={downloading}
                        onClick={() => {
                          var allDocuments = [];
                          var downloadDocuments = documents.filter(
                            x =>
                              x.documentType !== 'DataBook' &&
                              x.documentType !== 'MaintenanceManual' &&
                              x.documentType !== 'AssemblyAndDisassembly' &&
                              x.documentType !== 'GuideToEvaluatingParts'
                          );

                          function _addDocument(
                            docIdentifier,
                            condition = true
                          ) {
                            if (!!condition) {
                              allDocuments.push(docIdentifier);
                            }
                          }

                          function _addIdIfExists(documentType) {
                            downloadDocuments
                              .filter(x => x.documentType === documentType)
                              .forEach(doc => {
                                if (doc !== undefined) {
                                  allDocuments.push(doc.fileId);
                                }
                              });
                          }

                          _addDocument('BookCover');
                          _addDocument(
                            'CanisterGuard',
                            detail.canisterGuardLink
                          );
                          _addIdIfExists('DiagramPage');
                          _addDocument('PartsList');
                          _addDocument('Certificate', detail.testDate);
                          _addIdIfExists('TestChart');
                          _addIdIfExists('InspectionReport');
                          _addIdIfExists('MaterialCert');
                          _addDocument('License');
                          _addIdIfExists('MakeBreak');
                          _addIdIfExists('VendorCOC');
                          _addIdIfExists('CalculationSummary');
                          _addIdIfExists('CustomerPrint');

                          this._downloadAllDocuments(
                            detail.sysRowID,
                            allDocuments,
                            `mmvalve_${detail.serialNumber}_AllDocuments.pdf`
                          );
                        }}
                      >
                        <Icon name="download" />
                        &nbsp;DOWNLOAD ALL DOCUMENTATION
                      </Button>
                    )}
                  </LoadingContainer>
                </Styles>
              ))}
          </LoadingContainer>
        </Layout.Fill>
      );
    };

    const {close} = this.props;
    const {detail, documents, error} = this.state;

    const loadingDetail = !detail && !error;
    const loadingDocuments = !documents && !error;

    const panes = [
      {
        menuItem: 'General Info',
        render: () => (
          <GeneralInfo
            loadingDetail={loadingDetail}
            loadingDocuments={loadingDocuments}
            {...this.state}
          />
        ),
      },
      {
        menuItem: 'Parts',
        render: () => <Parts detail={detail} loadingDetail={loadingDetail} />,
      },
      {
        menuItem: 'Part Kits',
        render: () => (
          <PartKits detail={detail} loadingDetail={loadingDetail} />
        ),
      },
    ];

    if (
      detail &&
      detail.repairInfo &&
      !some(panes, x => x.menuItem === 'Repairs')
    ) {
      panes.push({
        menuItem: 'Repairs',
        render: () => <Repairs detail={detail} loadingDetail={loadingDetail} />,
      });
    }

    return (
      <Layout className="page-wrapper">
        <Header className="page-header">
          <Page.HeaderWithActions
            title={
              loadingDetail
                ? 'Loading'
                : error
                  ? 'Error'
                  : `Serial Number - ${detail.serialNumber}`
            }
          >
            {!error && !loadingDetail && (
              <Button
                icon={detail.hasFavorite ? 'star' : 'star outline'}
                size="mini"
                onClick={this._toggleFavorite}
              />
            )}
            <Button icon="close" size="mini" onClick={close} />
          </Page.HeaderWithActions>
        </Header>

        <Layout.Fill scroll>{detail && <Tab panes={panes} />}</Layout.Fill>
      </Layout>
    );
  }
}